/* eslint-disable no-unused-expressions */
/* eslint-disable max-len */
import { roundWithTaxPercent } from 'Helpers/utils';
import DropdownField from 'Modules/Formik/Dropdown/Dropdown';
import { parsePrescriptionConfig } from 'Redux/modules/webChat/prescription/actions';
import { Field, getIn } from 'formik';
import { debounce } from 'lodash';
import { arrayOf, func, number, shape, string } from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Icon, Input, Search } from 'semantic-ui-react';
import { CurrencyLabel } from 'Modules/Currency';
import { getDrugs } from 'Redux/modules/search/actions';

import styles from './MedicinePickerField.less';

const PAGE_SIZE_QUERY_TO_GLOCO = 30;

const resultRenderer = ({ name, vendor, unitPrice, id, currencySymbol }) => (
  <div className={styles.searchResults} key={id}>
    <div style={{ flex: 30 }}>
      <div style={{ marginBottom: '0.5rem' }}>{name}</div>
      <div>{vendor}</div>
    </div>
    <div style={{ flex: 6 }}>
      <span>
        {currencySymbol} <CurrencyLabel value={unitPrice} />
      </span>
    </div>
  </div>
);

resultRenderer.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  vendor: string.isRequired,
  unitPrice: string.isRequired,
  currencySymbol: string.isRequired,
};

const transform = (arr, taxPercent, currencySymbol) => {
  if (!arr?.length) return [];
  return arr.map(item => ({
    id: item.itemId,
    name: item.itemName,
    vendor: item.vendorName,
    unitPrice: roundWithTaxPercent(item.unitSellingPrice, taxPercent),
    uom: item.uOM,
    Dosage: item.dosage,
    taxPercent,
    quantity: item.stdAmountDispensed,
    currencySymbol,
  }));
};

const MedicinePickerField = props => {
  const {
    field: { name, value },
    form: { setFieldValue, errors },
    label,
    pharmacyId,
    resultSearchChange,
    quantity,
    uomArr,
    onChange,
    pharmacy,
    currencySymbol,
    serviceId,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [currentValue, setCurrentValue] = useState('');

  const dispatch = useDispatch();

  const taxPercent = pharmacy?.taxProfile?.taxPercent || 0;

  const handleParseData = async data => {
    const res = await dispatch(
      parsePrescriptionConfig({
        ...data,
        notTransformRequest: true,
      }),
    );
    if (res?.data?.uom) {
      setFieldValue('uomUnit', res?.data.uom?.toLowerCase());
    }
    if (res?.data?.dosageQuantity) {
      setFieldValue('amountPerTime', res?.data.dosageQuantity);
    }
    if (res?.data?.dosageUnit) {
      setFieldValue('doseUnit', res?.data.dosageUnit);
    }
    if (res?.data?.frequency) {
      setFieldValue('frequency', res?.data.frequency);
    }
  };

  const handleResultSelect = (e, { result }) => {
    setFieldValue(name, result);
    setFieldValue('quantity', result.quantity);
    handleParseData({ ...result, UOM: result.uom });
  };

  const handleSearchProduct = useCallback(
    debounce(async val => {
      if (val) {
        try {
          setIsLoading(true);
          setCurrentValue(val);

          const params = {
            pharmacy_id: pharmacyId,
            search_term: val,
            page_size: PAGE_SIZE_QUERY_TO_GLOCO,
            page: 1,
            teleconsult_service_id: serviceId,
          };

          const { data } = await dispatch(getDrugs(params));

          setIsLoading(false);
          setResults(transform(data.itemList, taxPercent, currencySymbol));

          resultSearchChange(transform(data.itemList, taxPercent, currencySymbol), currentValue);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error);
        }
      }
    }, 300),
    [],
  );

  const handleSearchChange = async (e, { value: val }) => {
    setCurrentValue(val);
    handleSearchProduct(val);
  };

  const quantiryErrorText = getIn(errors, 'quantity');

  return (
    <Form.Field className={styles.field}>
      <label htmlFor={name} className={styles.label}>
        {label}
      </label>
      {value && (
        <div className={styles.selectedMedicine}>
          <div
            style={{
              width: 56,
              height: 56,
              backgroundImage: 'url(https://react.semantic-ui.com/images/wireframe/image.png)',
              backgroundPosition: 'center',
              borderRadius: 4,
              overflow: 'hidden',
              marginRight: 16,
              backgroundSize: 'contain',
            }}
          />
          <div style={{ flex: 5 }}>
            <div style={{ marginBottom: '0.25rem' }}>{value.name}</div>
            <strong>
              {currencySymbol} <CurrencyLabel value={value.unitPrice} />
            </strong>
          </div>
          <div>
            <div style={{ flex: 2, display: 'flex', alignItems: 'center' }}>
              <div
                style={{
                  display: 'flex',
                  marginLeft: 'auto',
                  justifyContent: 'flex-end',
                }}
                className={styles.styleIcon}>
                <Icon
                  name="minus"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setFieldValue('quantity', parseInt(quantity, 10) - 1);
                  }}
                  disabled={quantity < 2}
                />
                <span className={styles.quantityInput} style={{ margin: '0 0.5rem' }}>
                  <Input
                    type="text"
                    value={quantity}
                    onChange={(e, data) => {
                      // only allow number input
                      if (!/^\d+$/.test(e.target.value) && e.target.value !== '') return;
                      setFieldValue('quantity', data.value);
                    }}
                  />
                </span>
                <Icon
                  name="plus"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    setFieldValue('quantity', parseInt(quantity, 10) + 1);
                  }}
                  disabled={+quantity === 999}
                />
              </div>
              <div style={{ flex: 1, marginLeft: 16 }}>
                <Field
                  name="uomUnit"
                  component={DropdownField}
                  options={uomArr}
                  onChange={onChange}
                />
              </div>
            </div>
            {quantiryErrorText && <div className={styles.error}>{quantiryErrorText}</div>}
          </div>
        </div>
      )}

      {!value && (
        <Search
          fluid
          loading={isLoading}
          onResultSelect={handleResultSelect}
          onSearchChange={handleSearchChange}
          results={results}
          value={currentValue}
          resultRenderer={resultRenderer}
          placeholder="Search medicine"
          noResultsMessage=""
          input={{ icon: 'search', iconPosition: 'left' }}
        />
      )}

      {/* {
        touch
        && errorText
        && <div className={styles.error}>{errorText}</div>
      } */}
    </Form.Field>
  );
};

MedicinePickerField.defaultProps = {
  uomArr: [],
  onChange: func,
  pharmacy: shape({}),
};
MedicinePickerField.propTypes = {
  field: shape({}).isRequired,
  resultSearchChange: shape({}).isRequired,
  form: shape({
    setFieldValue: func.isRequired,
    touched: shape({}).isRequired,
  }).isRequired,
  label: string.isRequired,
  pharmacyId: string.isRequired,
  quantity: number.isRequired,
  uomArr: arrayOf(shape({})),
  onChange: null,
  pharmacy: null,
  currencySymbol: string.isRequired,
  serviceId: string,
};

export default MedicinePickerField;
